import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  FormControl,
  Link,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { useVideoCatalog } from '../../../queries';
import type { VideoModelInfo, VideoModelTier, VideoOperationType } from '../../../domain';
import { availableModels, modelSupports } from './videoModelCapability';
import { formatEstimate, type NairaRate } from '../../../credits/naira';

const TIERS: VideoModelTier[] = ['value', 'standard', 'premium'];
const TIER_LABEL: Record<VideoModelTier, string> = {
  value: 'Value',
  standard: 'Standard',
  premium: 'Premium',
};

export interface ModelTierValue {
  modelTier: VideoModelTier;
  /** Advanced override — a specific model id. */
  model?: string;
}

export interface ModelTierPickerProps {
  value: ModelTierValue;
  onChange: (next: ModelTierValue) => void;
  /** The active op — filters which models/tiers are valid. */
  kind: VideoOperationType;
  /** Drives the inline cost delta. */
  durationSeconds: number;
  /** The wallet's Naira per dollar for video, shown beside each dollar price. */
  nairaRate?: NairaRate | null;
}

/** Representative (cheapest valid) model for a tier — used for the tier's price hint. */
function tierRepresentative(
  models: VideoModelInfo[],
  tier: VideoModelTier,
  kind: VideoOperationType,
): VideoModelInfo | undefined {
  return models
    .filter(m => m.tier === tier && modelSupports(m, kind))
    .sort((a, b) => (a.pricePerSecond ?? 0) - (b.pricePerSecond ?? 0))[0];
}

/**
 * Tier-first model selector (A-F5). The primary control is a Value/Standard/
 * Premium toggle; an "Advanced" disclosure reveals the specific model within
 * the tier. The model list is **read from the BE catalog** — never hardcoded —
 * so a monthly fal.ai refresh needs no FE deploy. Invalid op/model combos are
 * disabled with a reason.
 */
export const ModelTierPicker: React.FC<ModelTierPickerProps> = ({
  value,
  onChange,
  kind,
  durationSeconds,
  nairaRate,
}) => {
  const { data, isLoading } = useVideoCatalog();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const models = useMemo(() => availableModels(data), [data]);
  const tierReps = useMemo(
    () => Object.fromEntries(TIERS.map(t => [t, tierRepresentative(models, t, kind)])) as Record<
      VideoModelTier,
      VideoModelInfo | undefined
    >,
    [models, kind],
  );

  const modelsInTier = useMemo(
    () => models.filter(m => m.tier === value.modelTier),
    [models, value.modelTier],
  );

  const usd = (m?: VideoModelInfo) => (m?.pricePerSecond != null ? m.pricePerSecond * durationSeconds : null);
  const cost = (m?: VideoModelInfo) => {
    const price = usd(m);
    return (price != null ? formatEstimate(price, nairaRate) : null) ?? '';
  };
  /** The tier buttons' price: the same Naira estimate, or nothing until the rate is known. */
  const tierCost = (m?: VideoModelInfo) => cost(m) || null;

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Quality tier
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value.modelTier}
        onChange={(_e, next: VideoModelTier | null) => {
          if (next) onChange({ modelTier: next, model: undefined });
        }}
      >
        {TIERS.map(t => {
          const rep = tierReps[t];
          const disabled = !rep; // no valid model for this op in the tier
          return (
            <ToggleButton key={t} value={t} disabled={disabled} sx={{ flexDirection: 'column', py: 0.5 }}>
              <Typography variant="body2">{TIER_LABEL[t]}</Typography>
              <Typography variant="caption" color="text.secondary">
                {disabled ? 'n/a' : tierCost(rep)}
              </Typography>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>

      <Link
        component="button"
        type="button"
        variant="caption"
        underline="hover"
        onClick={() => setAdvancedOpen(o => !o)}
        sx={{ alignSelf: 'flex-start' }}
      >
        {advancedOpen ? 'Advanced ▾' : 'Advanced ▸'}
      </Link>

      <Collapse in={advancedOpen} unmountOnExit>
        <FormControl fullWidth size="small">
          <Select
            displayEmpty
            value={value.model ?? ''}
            onChange={e =>
              onChange({ modelTier: value.modelTier, model: e.target.value || undefined })
            }
            renderValue={selected => {
              if (!selected) return <em>Auto (cheapest in tier)</em>;
              return models.find(m => m.id === selected)?.displayName ?? selected;
            }}
          >
            <MenuItem value="">
              <em>Auto (cheapest in tier)</em>
            </MenuItem>
            {isLoading && <MenuItem disabled>Loading models…</MenuItem>}
            {modelsInTier.map(m => {
              const supported = modelSupports(m, kind);
              const item = (
                <MenuItem key={m.id} value={m.id} disabled={!supported}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2">{m.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      max {m.maxDuration}s{cost(m) ? ` · ${cost(m)}` : ''}
                      {!supported ? ` · unsupported for this op` : ''}
                    </Typography>
                  </Box>
                </MenuItem>
              );
              return supported ? (
                item
              ) : (
                <Tooltip key={m.id} title="This model can't run the selected operation" placement="left">
                  <span>{item}</span>
                </Tooltip>
              );
            })}
          </Select>
        </FormControl>
      </Collapse>
    </Stack>
  );
};

/** Resolve the model the panels should price against — the explicit override, else
 * the tier's representative. Exported for the cost-estimate readout in panels. */
export function resolvePricedModel(
  models: VideoModelInfo[],
  value: ModelTierValue,
  kind: VideoOperationType,
): VideoModelInfo | undefined {
  if (value.model) return models.find(m => m.id === value.model);
  return tierRepresentative(models, value.modelTier, kind);
}
